import {ToastAction} from "@src/ui/views/toast";
import {useVerifyPhoneOtpMutation} from "@src/features/auth/redux/auth-api";
import * as React from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {zodResolver} from "@hookform/resolvers/zod";
import {Button} from "@src/ui/views/button";
import {Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage} from "@src/ui/views/form";
import {InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot} from "@src/ui/views/input-otp";
import {REGEXP_ONLY_DIGITS} from "input-otp";
import {useForm} from "react-hook-form";
import {z} from "zod";


const OTP_LENGTH: number = 6;


const FormSchema = z.object({
	otpCode: z.string()
		.refine((val) => val.length === OTP_LENGTH, {message: `The OTP is a ${OTP_LENGTH}-digits code`})
});


type FormData = z.infer<typeof FormSchema>;


function createSuccessToast(): any { // todo use Toast {
	return {
		title: "Success!",
		description: "You will soon be redirected to the dashboard.",
		action: <ToastAction altText="Go To Dashboard">Go Now</ToastAction>
	};
}


function createFailureToast(): any { // todo use Toast {
	return {
		variant: "destructive",
		title: "Uh oh! Something went wrong.",
		description: "There was a problem with your request.",
		action: <ToastAction altText="OK">OK</ToastAction>
	};
}


export function VerifyOtpScreen() {
	const navigate = useNavigate();
	
	const location = useLocation();
	
	const phoneNumber = location.state?.phoneNumber || '';
	
	const [triggerVerifyPhoneOtp, verifyOtpPhoneState] = useVerifyPhoneOtpMutation();
	
	const onPhoneNumberRegret = React.useCallback(() => {
		navigate("/auth/login");
	}, [navigate]);
	
	const onSubmit = React.useCallback((data: FormData) => {
		const otpCode = data.otpCode;
		triggerVerifyPhoneOtp({phoneNumber: phoneNumber, otpCode: otpCode});
	}, [triggerVerifyPhoneOtp, phoneNumber]);
	
	const form = useForm<FormData>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			otpCode: ""
		}
	});
	
	return (
		<div className="flex w-full h-full flex-row items-center justify-center">
			<div className="flex flex-col justify-center p-4">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="space-y-8 flex flex-col items-start"
						key="VerificationCodeForm">
						<FormField
							control={form.control}
							name="otpCode"
							render={({field}) => (
								<FormItem>
									<FormLabel>Verification Code</FormLabel>
									<FormControl>
										<InputOTP
											maxLength={OTP_LENGTH}
											pattern={REGEXP_ONLY_DIGITS}
											{...field}
										>
											<InputOTPGroup>
												<InputOTPSlot index={0}/>
												<InputOTPSlot index={1}/>
												<InputOTPSlot index={2}/>
											</InputOTPGroup>
											<InputOTPSeparator/>
											<InputOTPGroup>
												<InputOTPSlot index={3}/>
												<InputOTPSlot index={4}/>
												<InputOTPSlot index={5}/>
											</InputOTPGroup>
										</InputOTP>
									</FormControl>
									<FormDescription className="text-left">
										Please enter the verification code sent to your phone.
									</FormDescription>
									<FormMessage/>
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							disabled={verifyOtpPhoneState.isLoading}
						>
							{verifyOtpPhoneState.isLoading ? "Verifying..." : "Submit"}
						</Button>
					</form>
				</Form>
				{phoneNumber &&
					<div className="mt-10">
						Verifying {phoneNumber}.{' '}
						<button
							onClick={onPhoneNumberRegret}
							className="text-blue-500 hover:underline"
						>
							Not your number?
						</button>
					</div>
				}
			</div>
		</div>
	);
}
